import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchTimeline, type TimelineItem, type TimelineView } from '../api/tests';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';

const when = (iso: string) => new Date(iso).toLocaleTimeString('ca');

/**
 * The picture behind a watcher screenshot or hotkey capture, when the
 * platform kept one a page may show: a withheld one (no attestation that it
 * is the game's window, WatcherService.shown) only for an admin.
 */
const captureUrl = (i: TimelineItem, isAdmin: boolean): string | null => {
  if (i.kind !== 'watcher_screenshot' && i.kind !== 'watcher_hotkey') return null;
  const payload = i.payload as { url?: unknown; withheld?: unknown } | null;
  if (payload?.withheld === true && !isAdmin) return null;
  return typeof payload?.url === 'string' && payload.url ? payload.url : null;
};

/**
 * The session page's "Proves" section (docs/design/HUMAN_TESTING_STUDY.md
 * §7): the tasks that ran in the session and the merged timeline the agent
 * reads, rendered for people. Only for somebody signed in: names are in it,
 * and the session page itself is public by link.
 */
export function SessionTests({ code }: { code: string }) {
  const t = ca.sessionTests;
  const isAdmin = useAuth().me.roles.includes('ADMIN');
  const [view, setView] = useState<TimelineView | null>(null);
  const [onlyTests, setOnlyTests] = useState(true);

  useEffect(() => {
    fetchTimeline(code)
      .then(setView)
      .catch(() => setView(null));
  }, [code]);

  if (!view) return null;
  const tasks = view.items.filter((i) => i.source === 'task');
  const items = onlyTests
    ? view.items.filter(
        (i) =>
          i.source === 'task' ||
          i.source === 'watcher' ||
          i.kind.startsWith('test') ||
          i.kind === 'script_error' ||
          i.kind === 'runtime_warning',
      )
    : view.items;
  return (
    <section className="card" aria-label={t.title}>
      <h2>{t.title}</h2>
      {tasks.length === 0 && (
        <p className="muted small">
          {t.none} <Link to="/proves">{ca.nav.tests}</Link>
        </p>
      )}
      <p className="muted small">
        {t.sources}:{' '}
        {Object.entries(view.sources)
          .map(([k, v]) => `${t.sourceNames[k] ?? k} ${sourceAmount(String(v))}`)
          .join(' · ')}
      </p>
      <p className="row">
        <button
          type="button"
          className={`chip ${onlyTests ? 'on' : ''}`}
          onClick={() => setOnlyTests(true)}
        >
          {t.showTests}
        </button>
        <button
          type="button"
          className={`chip ${onlyTests ? '' : 'on'}`}
          onClick={() => setOnlyTests(false)}
        >
          {t.showAll}
        </button>
      </p>
      <ul className="activity">
        {items.map((i, n) => {
          const url = captureUrl(i, isAdmin);
          return (
            <li key={`${i.at}-${n}`}>
              <span className="muted small num">{when(i.at)}</span> <code>{i.kind}</code>{' '}
              {i.summary}
              {url && (
                <a className="capture-thumb" href={url} target="_blank" rel="noreferrer">
                  <img src={url} alt={t.capture} loading="lazy" />
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * What each source brought, as the platform counts it ("30 events", "none"),
 * said in Catalan: the line read half in English on a Catalan page.
 */
function sourceAmount(raw: string): string {
  const units = ca.sessionTests.sourceUnits;
  if (raw === 'none' || raw === '') return units.none;
  const m = /^(\d+)\s+(\w+)$/.exec(raw);
  if (!m) return raw;
  const n = Number(m[1]);
  const unit = units[m[2]];
  return Array.isArray(unit) ? `${n} ${n === 1 ? unit[0] : unit[1]}` : raw;
}
